import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Stethoscope,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Loader2,
  AlertTriangle,
  BookOpen,
  GraduationCap,
  CheckCircle2,
  Circle,
  ListChecks,
  MessageSquare,
  RotateCcw,
  Flag,
  FileText,
} from "lucide-react";
import Markdown from "@/components/Markdown";
import CompletionCard from "@/components/CompletionCard";
import LibraryEntryDrawer from "@/components/LibraryEntryDrawer";
import ExportMenu from "@/components/ExportMenu";
import RichTextEditor from "@/components/RichTextEditor";
import { TRIAGE_TYPES, typeName, type TriageType, type TriageLens } from "@/content/triage";
import { useProgressStore } from "@/store/progressStore";
import { useCategoryStore } from "@/store/categoryStore";
import { useAIStore } from "@/store/aiStore";
import { useLenses } from "@/store/customContentStore";
import { useDesignDocStore } from "@/store/designDocStore";
import { getProvider, AIError } from "@/core/ai";
import type { CompletionResult } from "@/core/data/db";

type Phase = "setup" | "run" | "report";
type Mode = "lens" | "conversation";

const DRAFT_KEY = "hippo.triage.draft";
const answerKey = (lensId: string, qi: number) => `${lensId}:${qi}`;

interface FlatQuestion {
  lens: TriageLens;
  q: string;
  qi: number;
}

export default function Triage() {
  const navigate = useNavigate();
  const complete = useProgressStore((s) => s.complete);
  const setOnly = useCategoryStore((s) => s.setOnly);
  const aiConfigured = useAIStore((s) => s.isConfigured());
  const createDoc = useDesignDocStore((s) => s.create);

  const [phase, setPhase] = useState<Phase>("setup");
  const [type, setType] = useState<TriageType>("mechanic");
  const [mode, setMode] = useState<Mode>("lens");
  const [subject, setSubject] = useState("");
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [lensIdx, setLensIdx] = useState(0);
  const [qFlatIdx, setQFlatIdx] = useState(0);
  const [studyId, setStudyId] = useState<string | null>(null);
  const [completion, setCompletion] = useState<CompletionResult | null>(null);

  // Restore an in-progress triage.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw) as { type: TriageType; subject: string; answers: Record<string, string> };
        if (d.subject) {
          setType(d.type);
          setSubject(d.subject);
          setAnswers(d.answers ?? {});
        }
      }
    } catch {
      /* ignore */
    }
  }, []);

  // Autosave.
  useEffect(() => {
    if (subject.trim()) localStorage.setItem(DRAFT_KEY, JSON.stringify({ type, subject, answers }));
  }, [type, subject, answers]);

  const allLenses = useLenses();
  const lenses = useMemo(() => allLenses.filter((l) => l.appliesTo.includes(type)), [allLenses, type]);
  const flat = useMemo<FlatQuestion[]>(
    () => lenses.flatMap((lens) => lens.questions.map((q, qi) => ({ lens, q, qi }))),
    [lenses]
  );

  const setAnswer = (lensId: string, qi: number, v: string) =>
    setAnswers((a) => ({ ...a, [answerKey(lensId, qi)]: v }));

  const lensAddressed = (lens: TriageLens) => lens.questions.some((_, qi) => (answers[answerKey(lens.id, qi)] ?? "").trim());
  const addressedLenses = lenses.filter(lensAddressed);
  const coverage = lenses.length ? addressedLenses.length / lenses.length : 0;

  function startOver() {
    localStorage.removeItem(DRAFT_KEY);
    setAnswers({});
    setSubject("");
    setLensIdx(0);
    setQFlatIdx(0);
    setCompletion(null);
    setPhase("setup");
  }

  async function saveResult() {
    const disciplineIds = [...new Set(addressedLenses.map((l) => l.disciplineId).filter(Boolean))] as string[];
    const transcript = lenses
      .map((lens) => {
        const qa = lens.questions
          .map((q, qi) => {
            const ans = (answers[answerKey(lens.id, qi)] ?? "").trim();
            return ans ? `**${q}**\n${ans}` : null;
          })
          .filter(Boolean)
          .join("\n\n");
        return qa ? `## ${lens.name}\n${qa}` : null;
      })
      .filter(Boolean)
      .join("\n\n");

    const title = subject.trim().split("\n")[0].slice(0, 80) || "Untitled triage";
    const xp = 20 + Math.round(coverage * 20);
    const result = await complete(
      {
        refType: "triage",
        refId: `triage-${type}`,
        categoryIds: disciplineIds,
        title,
        context: `Triage · ${typeName(type)}`,
        response: `${subject.trim()}\n\n${transcript}`,
        score: Math.round(coverage * 100),
        createdAt: Date.now(),
      },
      xp
    );
    localStorage.removeItem(DRAFT_KEY);
    setCompletion(result);
  }

  function practice(disciplineId: string) {
    setOnly(disciplineId);
    navigate("/challenge");
  }

  function draftSpec() {
    const formatId = type === "system" ? "system-spec" : "feature-spec";
    const title = subject.trim().split("\n")[0].slice(0, 80) || "Untitled";
    const blank = lenses.filter((l) => !lensAddressed(l)).map((l) => l.name);
    const values: Record<string, string> = { premise: subject.trim() };
    if (blank.length) {
      values.risks = `Open questions from triage — lenses left unaddressed:\n${blank.map((n) => `- ${n}`).join("\n")}`;
    }
    const id = createDoc(formatId, { title, values });
    navigate("/docs", { state: { openDocId: id } });
  }

  // ---------------- Setup ----------------
  if (phase === "setup") {
    return (
      <div className="mx-auto max-w-2xl">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="grid h-16 w-16 place-items-center rounded-2xl bg-surface-2 text-primary">
            <Stethoscope className="h-8 w-8" />
          </div>
          <h2 className="font-display text-2xl font-semibold text-text">Triage a design</h2>
          <p className="max-w-xl text-muted">
            Describe a mechanic, system, or feature. Hippo interrogates it lens by lens — drawing on the whole study library — so gaps and blind spots surface before you build.
          </p>
        </div>

        <div className="mt-8 space-y-5">
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">What are you triaging?</div>
            <div className="grid gap-2 sm:grid-cols-3">
              {TRIAGE_TYPES.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setType(t.id)}
                  className={[
                    "rounded-xl border p-3 text-left transition-colors",
                    type === t.id ? "border-primary bg-primary/10 text-text" : "border-border bg-surface text-muted hover:text-text",
                  ].join(" ")}
                >
                  <div className="font-display font-semibold text-text">{t.name}</div>
                  <div className="mt-0.5 text-xs">{t.blurb}</div>
                </button>
              ))}
            </div>
          </div>

          <label className="block">
            <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-muted">Describe it</span>
            <RichTextEditor
              value={subject}
              onChange={setSubject}
              placeholder={`e.g. "A grappling hook that pulls the player toward any surface and can yank light enemies toward you…"`}
              minHeight="7rem"
            />
          </label>

          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">How do you want to work through it?</div>
            <div className="flex flex-wrap gap-2">
              <ModeBtn active={mode === "lens"} onClick={() => setMode("lens")} icon={ListChecks}>Lens by lens</ModeBtn>
              <ModeBtn active={mode === "conversation"} onClick={() => setMode("conversation")} icon={MessageSquare}>One question at a time</ModeBtn>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => { setLensIdx(0); setQFlatIdx(0); setPhase("run"); }}
              disabled={!subject.trim()}
              className="rounded-xl bg-primary px-6 py-3 font-display font-semibold text-primary-fg shadow-glow transition-transform enabled:hover:scale-[1.03] disabled:cursor-not-allowed disabled:opacity-40"
            >
              Start triage — {lenses.length} lenses
            </button>
            {Object.keys(answers).length > 0 && (
              <button onClick={startOver} className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-text">
                <RotateCcw className="h-4 w-4" /> Clear saved progress
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---------------- Report ----------------
  if (phase === "report") {
    const blank = lenses.filter((l) => !lensAddressed(l));
    return (
      <>
        <div className="mx-auto max-w-3xl space-y-5">
          <button onClick={() => setPhase("run")} className="inline-flex items-center gap-1.5 text-sm font-medium text-muted transition-colors hover:text-text">
            <ChevronLeft className="h-4 w-4" /> Back to questions
          </button>

          <div className="rounded-2xl border border-border bg-surface p-6">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted">
              <Flag className="h-4 w-4 text-primary" /> Gap report · {typeName(type)}
            </div>
            <h2 className="mt-2 font-display text-2xl font-bold text-text">{subject.trim().split("\n")[0].slice(0, 90)}</h2>

            {/* Coverage meter */}
            <div className="mt-4">
              <div className="mb-1 flex items-center justify-between text-sm">
                <span className="text-muted">Coverage</span>
                <span className="font-medium text-text">{addressedLenses.length}/{lenses.length} lenses · {Math.round(coverage * 100)}%</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-surface-2">
                <div className="h-full rounded-full bg-primary" style={{ width: `${Math.round(coverage * 100)}%` }} />
              </div>
            </div>

            {/* Lens grid */}
            <div className="mt-5 grid gap-2 sm:grid-cols-2">
              {lenses.map((l) => {
                const done = lensAddressed(l);
                return (
                  <div key={l.id} className={["flex items-center gap-2 rounded-lg border px-3 py-2", done ? "border-border bg-surface-2/50" : "border-amber-400/40 bg-amber-400/5"].join(" ")}>
                    {done ? <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" /> : <Circle className="h-4 w-4 shrink-0 text-amber-400" />}
                    <span className="flex-1 text-sm text-text">{l.name}</span>
                    {l.libraryEntryId && (
                      <button onClick={() => setStudyId(l.libraryEntryId!)} aria-label="Study" title="Study this" className="text-muted hover:text-primary"><BookOpen className="h-4 w-4" /></button>
                    )}
                    {l.disciplineId && (
                      <button onClick={() => practice(l.disciplineId!)} aria-label="Practice" title="Practice this" className="text-muted hover:text-primary"><GraduationCap className="h-4 w-4" /></button>
                    )}
                  </div>
                );
              })}
            </div>

            {blank.length > 0 && (
              <p className="mt-4 text-sm text-muted">
                <span className="font-medium text-amber-400">Unaddressed:</span> {blank.map((l) => l.name).join(", ")} — worth a pass before you commit.
              </p>
            )}
          </div>

          {/* Act on the triage: turn it into a spec, or export it */}
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={draftSpec} className="inline-flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/5 px-3 py-2 text-sm font-medium text-text transition-colors hover:bg-primary/10">
              <FileText className="h-4 w-4 text-primary" /> Draft a {type === "system" ? "System" : "Feature"} Spec from this
            </button>
            <ExportMenu baseName={subject.trim().split("\n")[0] || "triage"} getMarkdown={() => buildTranscript(type, subject, lenses, answers, true)} />
          </div>

          {/* AI completeness critique */}
          <AIBlock
            label="AI gap report"
            configured={aiConfigured}
            buildPrompt={() => ({
              system:
                `You are a sharp, constructive game-design mentor reviewing a designer's triage of a ${type}. Given their description and their answers across design lenses, write a concise gap report in GitHub-flavored Markdown: **What's solid** (2-3 bullets), **Biggest gaps / blind spots** (2-4 bullets referencing lenses they left thin or skipped), and **Ask yourself next** (2-3 pointed questions). Be specific to their answers, under ~280 words. No preamble.`,
              user: buildTranscript(type, subject, lenses, answers, true),
            })}
          />

          {/* Save / XP */}
          {completion ? (
            <CompletionCard result={completion} onNext={startOver} nextLabel="New triage" />
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <button onClick={saveResult} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-fg shadow-glow transition-transform hover:scale-[1.03]">
                <CheckCircle2 className="h-4 w-4" /> Save to history &amp; earn XP
              </button>
              <span className="text-xs text-muted">Saves your triage and awards XP scaled to coverage.</span>
            </div>
          )}
        </div>

        <LibraryEntryDrawer entryId={studyId} onClose={() => setStudyId(null)} />
      </>
    );
  }

  // ---------------- Run ----------------
  const finishBtn = (
    <button onClick={() => setPhase("report")} className="inline-flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/10 px-4 py-2 text-sm font-semibold text-text transition-colors hover:bg-primary/20">
      <Flag className="h-4 w-4 text-primary" /> Finish &amp; see report
    </button>
  );

  return (
    <>
      <div className="mx-auto max-w-3xl space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button onClick={() => setPhase("setup")} className="inline-flex items-center gap-1.5 text-sm font-medium text-muted transition-colors hover:text-text">
            <ChevronLeft className="h-4 w-4" /> Setup
          </button>
          <div className="flex items-center gap-2">
            <ModeBtn small active={mode === "lens"} onClick={() => setMode("lens")} icon={ListChecks}>Lens</ModeBtn>
            <ModeBtn small active={mode === "conversation"} onClick={() => setMode("conversation")} icon={MessageSquare}>One-by-one</ModeBtn>
          </div>
        </div>

        {/* Subject reminder */}
        <div className="rounded-xl border border-border bg-surface-2/50 px-4 py-3 text-sm">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted">{typeName(type)}: </span>
          <span className="text-text">{subject.trim().split("\n")[0].slice(0, 160)}</span>
        </div>

        {mode === "lens" ? (
          <LensView
            lens={lenses[lensIdx]}
            index={lensIdx}
            total={lenses.length}
            answers={answers}
            setAnswer={setAnswer}
            onStudy={setStudyId}
            aiConfigured={aiConfigured}
            type={type}
            subject={subject}
            onPrev={() => setLensIdx((i) => Math.max(0, i - 1))}
            onNext={() => (lensIdx + 1 < lenses.length ? setLensIdx((i) => i + 1) : setPhase("report"))}
          />
        ) : (
          <ConversationView
            item={flat[qFlatIdx]}
            index={qFlatIdx}
            total={flat.length}
            answers={answers}
            setAnswer={setAnswer}
            onStudy={setStudyId}
            onPrev={() => setQFlatIdx((i) => Math.max(0, i - 1))}
            onNext={() => (qFlatIdx + 1 < flat.length ? setQFlatIdx((i) => i + 1) : setPhase("report"))}
          />
        )}

        <div className="flex items-center justify-between">
          <span className="text-xs text-muted">{addressedLenses.length}/{lenses.length} lenses addressed</span>
          {finishBtn}
        </div>
      </div>

      <LibraryEntryDrawer entryId={studyId} onClose={() => setStudyId(null)} />
    </>
  );
}

// ---------------- Lens view ----------------
function LensView({
  lens, index, total, answers, setAnswer, onStudy, onPrev, onNext, aiConfigured, type, subject,
}: {
  lens: TriageLens;
  index: number;
  total: number;
  answers: Record<string, string>;
  setAnswer: (lensId: string, qi: number, v: string) => void;
  onStudy: (id: string) => void;
  onPrev: () => void;
  onNext: () => void;
  aiConfigured: boolean;
  type: TriageType;
  subject: string;
}) {
  return (
    <motion.div key={lens.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-border bg-surface p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-primary">Lens {index + 1} / {total}</div>
          <h3 className="mt-1 font-display text-xl font-semibold text-text">{lens.name}</h3>
          <p className="mt-0.5 text-sm text-muted">{lens.blurb}</p>
        </div>
        {lens.libraryEntryId && (
          <button onClick={() => onStudy(lens.libraryEntryId!)} className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-sm text-text transition-colors hover:border-primary/50 hover:bg-surface-2">
            <BookOpen className="h-4 w-4 text-primary" /> Study
          </button>
        )}
      </div>

      <div className="mt-5 space-y-4">
        {lens.questions.map((q, qi) => (
          <div key={qi} className="block">
            <span className="mb-1 block text-sm font-medium text-text">{q}</span>
            <RichTextEditor
              value={answers[`${lens.id}:${qi}`] ?? ""}
              onChange={(md) => setAnswer(lens.id, qi, md)}
              placeholder="Your answer… (leave blank to flag as a gap)"
              minHeight="4rem"
            />
          </div>
        ))}
      </div>

      <div className="mt-4">
        <AIBlock
          label="Dig deeper (AI)"
          configured={aiConfigured}
          buildPrompt={() => ({
            system: `You are a sharp game-design mentor helping interrogate a ${type}. Given the designer's description and their answers to this lens, ask 2-3 pointed follow-up questions that expose gaps, and name one blind spot they may have. Be concise, specific, and constructive. GitHub-flavored Markdown, no preamble.`,
            user:
              `# ${type}\n${subject.trim()}\n\n## Lens: ${lens.name}\n` +
              lens.questions.map((q, qi) => `Q: ${q}\nA: ${(answers[`${lens.id}:${qi}`] ?? "").trim() || "(blank)"}`).join("\n\n"),
          })}
        />
      </div>

      <div className="mt-5 flex items-center justify-between">
        <button onClick={onPrev} disabled={index === 0} className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm text-muted transition-colors enabled:hover:text-text disabled:opacity-40">
          <ChevronLeft className="h-4 w-4" /> Previous
        </button>
        <button onClick={onNext} className="inline-flex items-center gap-1.5 rounded-lg bg-surface-2 px-3 py-2 text-sm font-medium text-text transition-colors hover:bg-border/40">
          {index + 1 < total ? "Next lens" : "Finish"} <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </motion.div>
  );
}

// ---------------- Conversation view ----------------
function ConversationView({
  item, index, total, answers, setAnswer, onStudy, onPrev, onNext,
}: {
  item: FlatQuestion;
  index: number;
  total: number;
  answers: Record<string, string>;
  setAnswer: (lensId: string, qi: number, v: string) => void;
  onStudy: (id: string) => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  const { lens, q, qi } = item;
  return (
    <motion.div key={index} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-border bg-surface p-6">
      <div className="flex items-center justify-between gap-2">
        <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary">
          {lens.name}
          {lens.libraryEntryId && (
            <button onClick={() => onStudy(lens.libraryEntryId!)} aria-label="Study this lens" className="text-muted hover:text-primary"><BookOpen className="h-3.5 w-3.5" /></button>
          )}
        </div>
        <span className="text-xs text-muted">Question {index + 1} / {total}</span>
      </div>

      <p className="mt-3 font-display text-lg font-semibold leading-snug text-text">{q}</p>
      <div className="mt-3">
        <RichTextEditor
          value={answers[`${lens.id}:${qi}`] ?? ""}
          onChange={(md) => setAnswer(lens.id, qi, md)}
          placeholder="Think out loud… (leave blank to flag as a gap)"
          minHeight="6rem"
        />
      </div>

      <div className="mt-5 flex items-center justify-between">
        <button onClick={onPrev} disabled={index === 0} className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm text-muted transition-colors enabled:hover:text-text disabled:opacity-40">
          <ChevronLeft className="h-4 w-4" /> Previous
        </button>
        <button onClick={onNext} className="inline-flex items-center gap-1.5 rounded-lg bg-surface-2 px-3 py-2 text-sm font-medium text-text transition-colors hover:bg-border/40">
          {index + 1 < total ? "Next question" : "Finish"} <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </motion.div>
  );
}

// ---------------- Shared AI block ----------------
function AIBlock({ label, configured, buildPrompt }: { label: string; configured: boolean; buildPrompt: () => { system: string; user: string } }) {
  const apiKey = useAIStore((s) => s.apiKey);
  const model = useAIStore((s) => s.model);
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [out, setOut] = useState("");
  const [error, setError] = useState("");
  const abort = useRef<AbortController | null>(null);

  useEffect(() => () => abort.current?.abort(), []);

  if (!configured) {
    return null;
  }

  async function run() {
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    setStatus("loading");
    setError("");
    try {
      const { system, user } = buildPrompt();
      const md = await getProvider({ apiKey: apiKey.trim(), model }).ask(system, user, 1024, controller.signal);
      if (controller.signal.aborted) return;
      setOut(md);
      setStatus("done");
    } catch (e) {
      if (controller.signal.aborted || (e instanceof DOMException && e.name === "AbortError")) return;
      setError(e instanceof AIError ? e.message : "Something went wrong.");
      setStatus("error");
    }
  }

  if (status === "idle") {
    return (
      <button onClick={run} className="inline-flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2 text-sm font-medium text-text transition-colors hover:bg-primary/20">
        <Sparkles className="h-4 w-4 text-primary" /> {label}
      </button>
    );
  }
  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
      {status === "loading" && (
        <div className="flex items-center gap-2 text-sm text-muted"><Loader2 className="h-4 w-4 animate-spin text-primary" /> Thinking…</div>
      )}
      {status === "error" && (
        <div className="flex items-start gap-2 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
          <div><p className="text-text">{error}</p><button onClick={run} className="mt-1 text-xs font-medium text-amber-400 hover:underline">Try again</button></div>
        </div>
      )}
      {status === "done" && (
        <>
          <div className="mb-1 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="text-xs font-semibold uppercase tracking-wide text-primary">{label}</span>
            <button onClick={run} className="ml-auto inline-flex items-center gap-1 text-xs text-muted hover:text-text"><RotateCcw className="h-3 w-3" /> Regenerate</button>
          </div>
          <div className="text-sm leading-relaxed text-text"><Markdown source={out} /></div>
        </>
      )}
    </div>
  );
}

function buildTranscript(type: TriageType, subject: string, lenses: TriageLens[], answers: Record<string, string>, includeBlanks: boolean): string {
  const parts = [`# ${type}\n${subject.trim()}`];
  for (const lens of lenses) {
    const qa = lens.questions
      .map((q, qi) => {
        const a = (answers[`${lens.id}:${qi}`] ?? "").trim();
        if (!a && !includeBlanks) return null;
        return `Q: ${q}\nA: ${a || "(blank / not addressed)"}`;
      })
      .filter(Boolean)
      .join("\n");
    parts.push(`## ${lens.name}\n${qa}`);
  }
  return parts.join("\n\n");
}

function ModeBtn({ active, onClick, icon: Icon, children, small }: { active: boolean; onClick: () => void; icon: typeof ListChecks; children: React.ReactNode; small?: boolean }) {
  return (
    <button
      onClick={onClick}
      className={[
        "inline-flex items-center gap-1.5 rounded-lg border font-medium transition-colors",
        small ? "px-2.5 py-1 text-xs" : "px-3 py-2 text-sm",
        active ? "border-primary bg-primary/10 text-text" : "border-border bg-surface text-muted hover:text-text",
      ].join(" ")}
    >
      <Icon className={small ? "h-3.5 w-3.5" : "h-4 w-4"} /> {children}
    </button>
  );
}
